import { Announcement03, DotsVertical, FilePlus02, MessageTextSquare02, UserCheck01 } from "@untitledui/icons";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import type { CreatorMetricsSortColumn } from "@/api/creator-metrics";
import { usePagination } from "@/api/pagination";
import type { MerchantRecommendedCreator, RecommendationsFilters } from "@/api/recommendations";
import {
  useMerchantRecommendationRowsFetcher,
  useMerchantRecommendations,
  useMerchantRecommendationsCount,
} from "@/api/recommendations";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { BulkCohortButton } from "@/components/creator-cohorts/bulk-cohort-button";
import { CreatorCohortCellContent } from "@/components/creator-cohorts/creator-cohort-cell";
import { NETWORK_COLUMN_TOOLTIP, NetworkCellContent } from "@/components/creator-network/creator-network";
import { BulkTagButton } from "@/components/creator-tags/bulk-tag-button";
import { CreatorTagsCellContent } from "@/components/creator-tags/creator-tags-cell";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { CohortLibraryModal } from "@/pages/creator-cohorts/cohort-library-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { TagLibraryModal } from "@/pages/creator-tags/tag-library-modal";
import {
  GenderDemoCellContent,
  MainAgeDemoCellContent,
  MatchQualityCellContent,
  TrendNumberCellContent,
} from "@/pages/explorers/columns/shared-cells";
import { compactNumberFormatter } from "@/utils/formatNumber";
import { formatCreatorId } from "@/utils/formatters";
import { useCreatorMetricColumns } from "./columns/creator-metric-columns";
import { useCreatorColumnCatalog } from "./creator-columns";

const numberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
  maximumFractionDigits: 1,
});

type RecommendationSortColumn = Exclude<CreatorMetricsSortColumn, "creatorName">;

const SORT_VALUES: Record<RecommendationSortColumn, (row: MerchantRecommendedCreator) => number | null> = {
  matchQualityScore: (row) => row.matchQualityScore,
  gmv: (row) => row.gmvAmount,
  videos: (row) => row.videoCount,
  followerCount: (row) => row.followerCount,
  unitSales: (row) => row.unitSales,
  orders: (row) => row.orders,
  ordersPerWeek: (row) => row.ordersPerWeek,
  revenue: (row) => row.revenue,
  avgGmv: (row) => row.avgGmv,
  gpm: (row) => row.gpm,
  aov: (row) => row.aov,
  ctr: (row) => row.ctr,
  ctor: (row) => row.ctor,
  postsPerWeek: (row) => row.postsPerWeek,
};

type RecommendedCreatorData = {
  id: string;
  creatorId: string;
  handle: string | null;
  thumbnail: string | null;
  creatorName: string | null;
  tiktokOpenId: string;
};

type Props = {
  isCreator: boolean;
  onCreatorSelect: (creatorId: string) => void;
  filters?: RecommendationsFilters;
  filterActions?: ReactNode;
  filterSection?: ReactNode;
};

export const RecommendationsTable = ({ isCreator, onCreatorSelect, filters, filterActions, filterSection }: Props) => {
  const pagination = usePagination();
  const filtersKey = JSON.stringify(filters ?? {});
  useEffect(() => {
    pagination.setPage(1);
  }, [filtersKey, pagination.setPage]);
  const creatorTagsEnabled = useFeatureFlag("F-creator-tags");
  const showTagsColumn = creatorTagsEnabled;
  const showCohortColumn = isCreator;
  const [tagLibraryOpen, setTagLibraryOpen] = useState(false);
  const [cohortLibraryOpen, setCohortLibraryOpen] = useState(false);
  const { sort, setFromDescriptor, descriptor } = useSort<RecommendationSortColumn>("matchQualityScore");
  const sortByMatchQuality = useCallback(
    () => setFromDescriptor({ column: "matchQualityScore", direction: "descending" }),
    [setFromDescriptor],
  );
  const metricColumns = useCreatorMetricColumns<MerchantRecommendedCreator & { rowKey: string }>();
  const columnCatalog = useCreatorColumnCatalog({ creatorTagsEnabled });

  const { data } = useMerchantRecommendations({
    pagination: pagination.pagination,
    isCreator,
    filters,
  });
  const { data: countData } = useMerchantRecommendationsCount({ isCreator, filters });

  const total = useCache(countData?.total);
  const recommendations = data?.recommendations ?? [];

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  const [rowInviteCreatorId, setRowInviteCreatorId] = useState<string>();
  const [rowMessageCreatorId, setRowMessageCreatorId] = useState<string>();
  const [addToListsBulkOpen, setAddToListsBulkOpen] = useState(false);
  const [addToListsCreatorId, setAddToListsCreatorId] = useState<string>();

  const gmvFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    compactDisplay: "short",
  });

  // For brand creators, use the creatorId (UUID) as the row key so that
  // onCreatorSelect receives the id that useCreator / CreatorDetails expects.
  // For candidates, fall back to tiktokCreatorOpenId (used by CandidateDetails).
  const rowId = useCallback(
    (c: MerchantRecommendedCreator) => (isCreator && c.creatorId ? c.creatorId : c.tiktokCreatorOpenId),
    [isCreator],
  );

  const {
    selectedKeys: selected,
    selectedData: selectedCreatorsData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<MerchantRecommendedCreator, RecommendedCreatorData>({
    getKey: rowId,
    toData: useCallback(
      (c: MerchantRecommendedCreator) => ({
        id: rowId(c),
        creatorId: c.username,
        handle: c.nickname ?? c.username ?? null,
        thumbnail: c.avatar,
        creatorName: c.nickname,
        tiktokOpenId: c.tiktokCreatorOpenId,
      }),
      [rowId],
    ),
  });

  const handleSelectionChange = useMemo(
    () => createSelectionHandler(recommendations),
    [createSelectionHandler, recommendations],
  );

  const fetchChunk = useMerchantRecommendationRowsFetcher({ isCreator, filters });
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total: total ?? 0,
    loadedCount: recommendations.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify({ isCreator, filters }),
  });

  const sortedRecommendations = useMemo(() => {
    if (!recommendations.length) return recommendations;
    const direction = sort.direction === "asc" ? 1 : -1;
    const value = SORT_VALUES[sort.column];
    return [...recommendations].sort((a, b) => ((value(a) ?? 0) - (value(b) ?? 0)) * direction);
  }, [recommendations, sort]);

  // UnboxTable's `rowId` needs a plain field key, but the real identity here is
  // conditional on `isCreator` (see `rowId` above) — synthesize it onto each row.
  const rows = useMemo(
    () => (data ? sortedRecommendations.map((r) => ({ ...r, rowKey: rowId(r) })) : undefined),
    [data, sortedRecommendations, rowId],
  );

  const columns = useMemo<UnboxTableColumn<(typeof sortedRecommendations)[number] & { rowKey: string }>[]>(() => {
    const cols: UnboxTableColumn<(typeof sortedRecommendations)[number] & { rowKey: string }>[] = [
      {
        id: "creatorName",
        label: "Creator",
        minWidth: "280px",
        render: (_value, row) => (
          <div className="flex items-center gap-3">
            {row.avatar ? (
              <img
                src={row.avatar}
                alt={row.nickname ?? "Creator"}
                className="h-10 w-10 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 flex-shrink-0">
                <span className="text-sm font-medium text-primary">{row.nickname?.charAt(0).toUpperCase() ?? "?"}</span>
              </div>
            )}
            <div className="flex flex-col">
              <div className="text-sm font-medium text-primary">{row.nickname ?? row.username}</div>
              <div className="text-xs text-tertiary">{formatCreatorId(row.username)}</div>
            </div>
          </div>
        ),
      },
    ];

    if (showTagsColumn) {
      cols.push({
        id: "tags",
        label: "Tags",
        minWidth: "180px",
        render: (_value, row) => (
          <CreatorTagsCellContent
            tiktokCreatorOpenId={row.tiktokCreatorOpenId}
            tagIds={row.tagIds}
            maxVisible={3}
            onManageAllTags={() => setTagLibraryOpen(true)}
          />
        ),
      });
    }

    if (showCohortColumn) {
      cols.push({
        id: "cohort",
        label: "Cohort",
        minWidth: "160px",
        render: (_value, row) =>
          row.creatorId ? (
            <CreatorCohortCellContent
              creatorId={row.creatorId}
              cohortId={row.cohortId}
              appliedBy={row.cohortAppliedBy}
              onManageAllCohorts={() => setCohortLibraryOpen(true)}
            />
          ) : null,
      });
    }

    cols.push({
      id: "network",
      label: "Network",
      tooltip: NETWORK_COLUMN_TOOLTIP,
      minWidth: "130px",
      render: (_value, row) => <NetworkCellContent network={row.network} />,
    });

    cols.push(
      {
        id: "matchQualityScore",
        label: "Match Quality",
        sortable: true,
        minWidth: "180px",
        render: (_value, row) => <MatchQualityCellContent score={row.matchQualityScore} />,
      },
      {
        id: "gmv",
        label: "GMV (Last 30 Days)",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => (
          <TrendNumberCellContent value={row.gmvAmount} trend={row.trendGmv} format={gmvFormatter.format} />
        ),
      },
      {
        id: "topGender",
        label: "Gender Demographic",
        minWidth: "160px",
        render: (_value, row) => <GenderDemoCellContent genderDistribution={row.genderDistribution} />,
      },
      {
        id: "topAgeRange",
        label: "Main Age Demo",
        minWidth: "120px",
        render: (_value, row) => <MainAgeDemoCellContent topAgeRange={row.topAgeRange} />,
      },
      {
        id: "videos",
        label: "Videos (Last 30 Days)",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) => (
          <TrendNumberCellContent value={row.videoCount} trend={row.trendVideos} format={numberFormatter.format} />
        ),
      },
      {
        id: "followerCount",
        label: "Followers",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) => (row.followerCount ? numberFormatter.format(row.followerCount) : "-"),
      },
      ...(isCreator ? metricColumns : []),
      {
        id: "list-actions",
        label: "",
        minWidth: "60px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center justify-end">
            <ButtonUtility
              size="xs"
              color="tertiary"
              tooltip="Invite to collaboration"
              icon={Announcement03}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setRowInviteCreatorId(row.tiktokCreatorOpenId);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Creator actions"
                >
                  <DotsVertical className="w-5 h-5 text-tertiary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setRowInviteCreatorId(row.tiktokCreatorOpenId);
                  }}
                >
                  <Announcement03 className="mr-2 size-4" />
                  Invite to collaboration
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setRowMessageCreatorId(row.tiktokCreatorOpenId);
                  }}
                >
                  <MessageTextSquare02 className="mr-2 size-4" />
                  Message Creator on TikTok
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddToListsCreatorId(row.tiktokCreatorOpenId);
                  }}
                >
                  <FilePlus02 className="mr-2 size-4" />
                  Add to List
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    );

    return cols;
  }, [showTagsColumn, showCohortColumn, gmvFormatter, isCreator, metricColumns]);

  return (
    <>
      {selected.length === 0 ? (
        <>
          <div className="flex flex-row items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              {countData
                ? `${compactNumberFormatter.format(countData.total)} ${isCreator ? "Creators" : "Candidates"}`
                : "Loading..."}
            </div>
            {filterActions}
          </div>
          {filterSection}
        </>
      ) : (
        <div className="flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
          <div className="flex flex-row items-center gap-2">
            <CloseButton onClick={selectAll.clear} />
            <span className="text-sm">{selectedCount.toLocaleString()} Selected</span>
            <SelectAllPagesButton state={selectAll} />
          </div>
          <div className="flex flex-row gap-2 items-center">
            <Button
              iconLeading={Announcement03}
              isDisabled={selectAll.isFetching}
              onClick={() => setBulkInviteModalOpen(true)}
            >
              Invite to Collab
            </Button>
            <Button
              iconLeading={MessageTextSquare02}
              isDisabled={selectAll.isFetching}
              onClick={() => setMessageDialogOpen(true)}
            >
              Message
            </Button>
            <MessageCreatorsDialog
              recipients={Array.from(selectedCreatorsData.values())}
              open={messageDialogOpen}
              onOpenChange={setMessageDialogOpen}
              onSuccess={selectAll.clear}
            />
            {showTagsColumn ? (
              <BulkTagButton
                creators={[...selected].map((id) => ({
                  tiktokCreatorOpenId: selectedCreatorsData.get(id)?.tiktokOpenId ?? null,
                  label: selectedCreatorsData.get(id)?.handle ?? undefined,
                }))}
                onApplied={selectAll.clear}
              />
            ) : (
              <Button
                iconLeading={FilePlus02}
                isDisabled={selectAll.isFetching}
                onClick={() => setAddToListsBulkOpen(true)}
              >
                Add to list
              </Button>
            )}
            {showCohortColumn && (
              <BulkCohortButton
                creatorIds={[...selected]}
                onApplied={selectAll.clear}
                getCreatorLabel={(id) => selectedCreatorsData.get(id)?.handle ?? undefined}
              />
            )}
          </div>
        </div>
      )}

      <UnboxTable<(typeof sortedRecommendations)[number] & { rowKey: string }>
        aria-label="Recommended Creators"
        columns={columns}
        rows={rows}
        rowId="rowKey"
        onClickRow={(row) => onCreatorSelect(rowId(row))}
        selection={{ raw: true, selectedKeys: new Set(selected), onSelectionChange: handleSelectionChange }}
        sorting={{ descriptor, onSortChange: setFromDescriptor }}
        columnCatalog={isCreator ? columnCatalog : undefined}
        onHiddenSortColumn={sortByMatchQuality}
        pagination={{
          page: pagination.page,
          totalPages: Math.ceil((total ?? 0) / pagination.pagination.limit),
          onPageChange: pagination.setPage,
        }}
        hasActiveFilters
        emptyState={{ icon: UserCheck01, title: "All caught up on recommendations" }}
        filteredEmptyState={{
          icon: UserCheck01,
          title: isCreator ? "All caught up on matches" : "All caught up on recommendations",
          description: "Try a different group, or browse all creators and filter by what matters to you.",
        }}
        className="max-h-[60vh]"
      />

      {/* Invite to Collaboration Modal */}
      <InviteToCollaborationModal
        creators={Array.from(selectedCreatorsData.values()).map((c) => ({
          openId: c.tiktokOpenId,
          username: c.creatorId,
          nickname: c.creatorName,
          avatar: c.thumbnail,
        }))}
        open={bulkInviteModalOpen}
        onOpenChange={setBulkInviteModalOpen}
        onSuccess={selectAll.clear}
      />

      {/* Bulk add to lists — fallback while the creator-tags FF is off */}
      <AddtoListsModal
        selectedCreators={Array.from(selectedCreatorsData.values()).map((c) => c.tiktokOpenId)}
        onAddToList={selectAll.clear}
        open={addToListsBulkOpen}
        onOpenChange={setAddToListsBulkOpen}
      />

      {/* Row-level add to lists */}
      <AddtoListsModal
        selectedCreators={addToListsCreatorId ? [addToListsCreatorId] : []}
        onAddToList={() => setAddToListsCreatorId(undefined)}
        open={addToListsCreatorId !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddToListsCreatorId(undefined);
        }}
      />

      {/* Row-level invite to collaboration */}
      {rowInviteCreatorId && (
        <InviteToCollaborationModal
          creators={sortedRecommendations
            .filter((c) => c.tiktokCreatorOpenId === rowInviteCreatorId)
            .map((c) => ({
              openId: c.tiktokCreatorOpenId,
              username: c.username,
              nickname: c.nickname,
              avatar: c.avatar,
            }))}
          open={!!rowInviteCreatorId}
          onOpenChange={(open) => !open && setRowInviteCreatorId(undefined)}
        />
      )}

      {/* Row-level message creator */}
      {rowMessageCreatorId && (
        <MessageCreatorsDialog
          recipients={sortedRecommendations
            .filter((c) => c.tiktokCreatorOpenId === rowMessageCreatorId)
            .map((c) => ({
              id: c.tiktokCreatorOpenId,
              creatorId: c.username,
              handle: c.nickname ?? c.username ?? null,
              thumbnail: c.avatar,
            }))}
          open={!!rowMessageCreatorId}
          onOpenChange={(open) => !open && setRowMessageCreatorId(undefined)}
          onSuccess={() => setRowMessageCreatorId(undefined)}
        />
      )}

      {showTagsColumn && <TagLibraryModal open={tagLibraryOpen} onOpenChange={setTagLibraryOpen} />}
      {showCohortColumn && <CohortLibraryModal open={cohortLibraryOpen} onOpenChange={setCohortLibraryOpen} />}
    </>
  );
};
